import React, { useEffect, lazy, Suspense } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import useAuthStore from './store/authStore';
import { usePushNotificacao } from './hooks/usePushNotificacao';
import RotaProtegida from './components/RotaProtegida';
import AcessoNegado from './components/AcessoNegado';
import ErrorBoundary from './components/ErrorBoundary';
import OfflineBanner from './components/OfflineBanner';
import InstalarPWA from './components/InstalarPWA';
import { ToastProvider } from './components/Toast';
import AlertaDiario from './components/AlertaDiario';
import OnboardingColab from './components/OnboardingColab';
import PushBloqueadoBanner from './components/PushBloqueadoBanner';
import VerificarCertificado from './VerificarCertificado';

// Carregamento imediato (sempre usados)
import Login from './Login';
import Layout from './Layout';
import Dashboard from './Dashboard';

// Lazy — carregados só quando a rota é acessada
const MeusTreinamentos  = lazy(() => import('./MeusTreinamentos'));
const Biblioteca        = lazy(() => import('./Biblioteca'));
const Competencias      = lazy(() => import('./Competencias'));
const Pesquisas         = lazy(() => import('./Pesquisas'));
const Colaboradores     = lazy(() => import('./Colaboradores'));
const ColaboradorDetalhe= lazy(() => import('./ColaboradorDetalhe'));
const Relatorios        = lazy(() => import('./Relatorios'));
const Configuracoes     = lazy(() => import('./Configuracoes'));
const MatrizPermissoes  = lazy(() => import('./MatrizPermissoes'));
const Mascaras          = lazy(() => import('./Mascaras'));
const Auditoria         = lazy(() => import('./Auditoria'));
const Perfil            = lazy(() => import('./Perfil'));
const Agenda            = lazy(() => import('./Agenda'));
const PlayerTreinamento = lazy(() => import('./PlayerTreinamento'));
const Certificados = lazy(() => import('./Certificados'));
const Notificacoes = lazy(() => import('./Notificacoes'));
const Solicitacoes = lazy(() => import('./Solicitacoes'));
const SaudeDoSistema = lazy(() => import('./SaudeDoSistema'));
const TrilhaAprendizado = lazy(() => import('./TrilhaAprendizado'));
const PlanoTreinamentos = lazy(() => import('./PlanoTreinamentos'));

const cor = '#B51013';

function LoadingPage() {
  return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f5f5f5' }}>
      <div style={{ textAlign: 'center' }}>
        <div style={{ width: '40px', height: '40px', borderRadius: '50%', border: `3px solid #f0f0f0`, borderTop: `3px solid ${cor}`, margin: '0 auto 12px', animation: 'spin 0.8s linear infinite' }} />
        <div style={{ fontSize: '12px', color: '#aaa' }}>Carregando...</div>
      </div>
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}

function App() {
  const inicializar = useAuthStore((s) => s.inicializar);
  usePushNotificacao();

  useEffect(() => {
    const unsub = inicializar();
    return unsub;
  }, [inicializar]);

  return (
    <ErrorBoundary>
      <ToastProvider>
      <BrowserRouter>
        <Suspense fallback={<LoadingPage />}>
          <Routes>
            <Route path="/login" element={<Login />} />
            <Route path="/acesso-negado" element={<AcessoNegado />} />
            <Route path="/verificar/:serial" element={<VerificarCertificado />} />

            <Route path="/" element={<RotaProtegida><Layout /></RotaProtegida>}>
              <Route index element={<Navigate to="/dashboard" replace />} />
              <Route path="dashboard"          element={<RotaProtegida acao="verDashboard"><Dashboard /></RotaProtegida>} />
              <Route path="meus-treinamentos"  element={<RotaProtegida acao="verMeusTreinamentos"><MeusTreinamentos /></RotaProtegida>} />
              <Route path="biblioteca"         element={<RotaProtegida acao="verBiblioteca"><Biblioteca /></RotaProtegida>} />
              <Route path="competencias"       element={<RotaProtegida acao="verCompetencias"><Competencias /></RotaProtegida>} />
              <Route path="pesquisas"          element={<RotaProtegida acao="verPesquisas"><Pesquisas /></RotaProtegida>} />
              <Route path="colaboradores"      element={<RotaProtegida acao="verColaboradores"><Colaboradores /></RotaProtegida>} />
              <Route path="colaboradores/:id"  element={<RotaProtegida acao="verColaboradores"><ColaboradorDetalhe /></RotaProtegida>} />
              <Route path="relatorios"         element={<RotaProtegida acao="verRelatorios"><Relatorios /></RotaProtegida>} />
              <Route path="configuracoes"      element={<RotaProtegida acao="verConfiguracoes"><Configuracoes /></RotaProtegida>} />
              <Route path="permissoes"         element={<RotaProtegida acao="verPermissoes"><MatrizPermissoes /></RotaProtegida>} />
              <Route path="mascaras"           element={<RotaProtegida acao="gerenciarMascaras"><Mascaras /></RotaProtegida>} />
              <Route path="auditoria"          element={<RotaProtegida acao="verAuditoria"><Auditoria /></RotaProtegida>} />
              <Route path="perfil"             element={<RotaProtegida><Perfil /></RotaProtegida>} />
              <Route path="agenda"             element={<RotaProtegida acao="verAgenda"><Agenda /></RotaProtegida>} />
              <Route path="treinamento/:nome"  element={<RotaProtegida acao="verMeusTreinamentos"><PlayerTreinamento /></RotaProtegida>} />
          <Route path="notificacoes"       element={<RotaProtegida><Notificacoes /></RotaProtegida>} />
          <Route path="solicitacoes"       element={<RotaProtegida acao="verColaboradores"><Solicitacoes /></RotaProtegida>} />
          <Route path="saude"              element={<RotaProtegida acao="verPermissoes"><SaudeDoSistema /></RotaProtegida>} />
          <Route path="trilha"             element={<RotaProtegida acao="verMeusTreinamentos"><TrilhaAprendizado /></RotaProtegida>} />
          <Route path="plano"              element={<RotaProtegida acao="verColaboradores"><PlanoTreinamentos /></RotaProtegida>} />
          <Route path="certificados"       element={<RotaProtegida acao="verMeusTreinamentos"><Certificados /></RotaProtegida>} />
            </Route>

            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </Suspense>
        <InstalarPWA />
        <OfflineBanner />
        <PushBloqueadoBanner />
        <AlertaDiario />
        <OnboardingColab />
      </BrowserRouter>
      </ToastProvider>
    </ErrorBoundary>
  );
}

export default App;
